import { z } from "zod";

export const BlisterSettingsSchema = z
  .object({
    colors: z.array(z.string().regex(/^#([0-9a-fA-F]{6})$/)).length(6),
    particleSize: z.number().min(0.1).max(1.8),
    speed: z.number().min(0.01).max(0.24),
    count: z.number().min(100).max(5000),
    opacity: z.number().min(0.2).max(1),
    additive: z.boolean(),
    speedRandomness: z.number().min(0).max(2.4),
    sizeRandomness: z.number().min(0).max(3),
    glowStrength: z.number().min(0).max(20),
    blurStrength: z.number().min(0).max(20),
    glowFraction: z.number().min(0.02).max(1),
    blurFraction: z.number().min(0.02).max(1),
    windStrength: z.number().min(0).max(2),
    windFraction: z.number().min(0).max(1),
    atmosphereIntensity: z.number().min(0).max(1.4),
    ambientDriftSpeed: z.number().min(0).max(1.2),
    hazeAmount: z.number().min(0).max(1.2),
    fieldDistortion: z.number().min(0).max(1.2),
    depthSoftness: z.number().min(0).max(1.2),
    sparkleCount: z.number().int().min(120).max(3200),
    sparkleSize: z.number().min(0.08).max(0.8),
    sparkleOpacity: z.number().min(0.06).max(0.9),
    sparkleReactivity: z.number().min(0).max(1.2),
    sparkleRandomness: z.number().min(0).max(1.2),
    pulseIntensity: z.number().min(0).max(0.8),
    pulseFrequency: z.number().min(0).max(0.85),
    trailAmount: z.number().min(0).max(0.5),
    afterimageAmount: z.number().min(0).max(0.42),
    flashSoftness: z.number().min(0.1).max(1),
    bloomIntensity: z.number().min(0).max(2.2),
    bloomRadius: z.number().min(0).max(1),
    grainAmount: z.number().min(0).max(0.22),
    vignetteAmount: z.number().min(0).max(1),
    chromaticAberration: z.number().min(0).max(0.35),
    despawnZ: z.number().optional(),
    spawnZ: z.number().optional(),
  })
  .strip();

export const KaleidoscopeSettingsSchema = z
  .object({
    colors: z.array(z.string().regex(/^#([0-9a-fA-F]{6})$/)).length(6),
    sourceImageIndex: z.number().int().min(0).max(4),
    segments: z.number().int().min(6).max(18),
    facetDensity: z.number().min(0.2).max(1.2),
    patternComplexity: z.number().min(0.2).max(1.2),
    centerStructure: z.number().min(0).max(1.2),
    outerReflection: z.number().min(0).max(1.2),
    layerDepth: z.number().min(0.1).max(1.1),
    rotationSpeed: z.number().min(0).max(1.2),
    morphSpeed: z.number().min(0).max(1.1),
    shimmerSpeed: z.number().min(0).max(1.2),
    pulseStrength: z.number().min(0).max(0.9),
    driftAmount: z.number().min(0).max(1.1),
    glowStrength: z.number().min(0.2).max(1.4),
    bloomRadius: z.number().min(0.08).max(0.7),
    highlightIntensity: z.number().min(0).max(1.2),
    refractionStrength: z.number().min(0).max(0.8),
    contrast: z.number().min(0.2).max(1.2),
    sparkleDensity: z.number().min(0).max(1),
    microFacetStrength: z.number().min(0).max(1.2),
    textureAmount: z.number().min(0).max(1.2),
    paletteIntensity: z.number().min(0.35).max(1.4),
    colorSeparation: z.number().min(0).max(1.2),
    darkLightBalance: z.number().min(0).max(1.2),
  })
  .strip();

export const FractalSettingsSchema = z
  .object({
    fractalFamily: z.enum(["cathedral", "nebula", "crystal", "mandala", "monolith"]),
    fractalVariant: z.enum(["infiniteZoom", "flarebrot", "voxelTunnel", "planarLight"]),
    colors: z.array(z.string().regex(/^#([0-9a-fA-F]{6})$/)).length(6),
    intensity: z.number().min(0).max(1),
    brightness: z.number().min(0).max(1),
    contrast: z.number().min(0).max(1),
    scale: z.number().min(0).max(1),
    symmetry: z.number().min(0).max(1),
    glow: z.number().min(0).max(1),
    vignette: z.number().min(0).max(1),
    grain: z.number().min(0).max(1),
    paletteIntensity: z.number().min(0).max(1.2),
    hueShift: z.number().min(-1).max(1),
    saturation: z.number().min(0).max(1),
    colorDrift: z.number().min(0).max(1),
    motionSpeed: z.number().min(0).max(1),
    rotationSpeed: z.number().min(0).max(1),
    drift: z.number().min(0).max(1),
    breathing: z.number().min(0).max(1),
    shimmer: z.number().min(0).max(1),
    audioReactive: z.boolean(),
    bassImpact: z.number().min(0).max(1),
    midImpact: z.number().min(0).max(1),
    trebleImpact: z.number().min(0).max(1),
    beatPulse: z.number().min(0).max(1),
    audioSmoothing: z.number().min(0).max(1),
    fractalDepth: z.number().min(0).max(1),
    voxelMaterialType: z.number().min(0).max(2),
    voxelRoughness: z.number().min(0).max(1),
    foldStrength: z.number().min(0).max(1),
    repetition: z.number().min(0).max(1),
    fieldWarp: z.number().min(0).max(1),
    detail: z.number().min(0).max(1),
  })
  .strip();

export type BlisterSettingsInput = z.input<typeof BlisterSettingsSchema>;
export type BlisterSettingsOutput = z.output<typeof BlisterSettingsSchema>;
export type KaleidoscopeSettingsInput = z.input<typeof KaleidoscopeSettingsSchema>;
export type KaleidoscopeSettingsOutput = z.output<typeof KaleidoscopeSettingsSchema>;
export type FractalSettingsInput = z.input<typeof FractalSettingsSchema>;
export type FractalSettingsOutput = z.output<typeof FractalSettingsSchema>;
